/**
 * Events menu: clickable list of events/custom events to go inside mp-drop-menu
 */

import debounce from 'lodash/debounce';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import {findKeyByValue} from 'common/util/typescript';

import {SectionItem, Section} from 'common/components/items-menu/types';
import {TooltipData} from 'common/components/select/types';
import topEventsClient from 'common/report/queries/query-api/top-events-client';
import {SPECIAL_EVENTS} from 'common/report/constants';
import customEventsClient from 'common/report/queries/app-api/custom-events-client';
import {getIconForEvent, renameEvent} from 'common/report/util';
import {EventDefinition} from 'common/report/queries/app-api/data-definitions-types';
import {getNonAutotrackCustomEvents, hasUserFilters} from 'common/util/custom_events';
import {fetchEventDefinitions} from 'common/util/definitions';
import {registerMPElement} from 'common/util/register-element';
import {getTooltipDataForEvent} from 'common/widgets/context-panel/util';
import {TRACKING_EVENTS} from 'common/widgets/query-block/constants';

import {ChildMenu} from '../child-menu';
import template from '../child-menu-items-menu.jade';
import {ANY_EVENT, SESSION_END_EVENT, SESSION_START_EVENT} from './constants';
import {EventItem, EVENT_PROPERTIES} from './types';
import {getCustomEventMenuItem, orderByLabel} from '../util';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types';

interface KeyedEventDataMap {
  [s: string]: EventItem;
}

interface StagedTag {
  type: string;
  name: string;
  count?: number;
}

interface EventsMenuState {
  eventData: EventItem[];
  // We store a keyed and identical set of eventData for faster lookups by id
  keyedEventData: KeyedEventDataMap;
  eventDefinitions: EventDefinition[];
  isMenuLoading: boolean;
  tooltipData: TooltipData;
  permissions: Set<string>;
  stagedTag: StagedTag;
}

class EventsMenu extends ChildMenu<EventsMenuState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      // crazy hack: Please DO NOT use `sections`, this is a temp workaround for signal to display context panel
      // TODO: Zihe remove this attr
      sections: {type: `json`, default: []},
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-top-events': `boolean`,
      'hide-edit-controls': {
        type: `boolean`,
        description: `show controls that popup modals to edit entities like custom events and properties`,
      },
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case `data-group-id`:
      case `exclude-autotrack-events`: {
        this.fetchEventData();
        break;
      }
    }
  }

  get config() {
    return {
      template,

      defaultState: {
        eventData: [],
        keyedEventData: {},
        eventDefinitions: [],
        isMenuLoading: false,
        tooltipData: null,
        permissions: null,
        stagedTag: null,
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => this.getSections(),
        handleItemClick: (ev) => this.handleItemClick(ev.detail.item),
        showItemDetails: debounce((ev) => this.showItemDetails(ev.detail.item), 100),
        sectionFromInput: (input) => {
          const item = {
            label: input,
            value: input,
            icon: `plus`,
          };
          return {items: [item]};
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchEventDefinitions();
    this.fetchEventData();
  }

  getItemUniqueKey(item) {
    return item.value;
  }

  showCreationControls() {
    return !this.state.stagedTag;
  }

  handleCloseMenu() {
    this.update({stagedTag: null});
  }

  handleItemClick(selected) {
    if (selected.value && selected.value.type === `tag`) {
      this.trackEvent(TRACKING_EVENTS.LEXICON_TAG_SELECTED, {
        tag: selected.value.name,
        eventsCount: selected.value.count,
      });

      this.update({
        stagedTag: selected.value as StagedTag,
      });
      return;
    }

    const item: EventItem = pick(selected, EVENT_PROPERTIES);

    if (!findKeyByValue(SPECIAL_EVENTS, item.value)) {
      this.addRecentItem(ResourceType.Event, item);
    }

    this.trackSelection(this.trackingProps(item));
    this.dispatchEvent(new CustomEvent(`select`, {detail: {item}}));
    this.update({stagedTag: null});
  }

  showItemDetails(item) {
    const {permissions, eventDefinitions} = this.state;
    let mpEvent = item;
    // TODO: Zihe will refactor signal so this transformation is not needed.
    if (item && item.value && item.value.kind === `event`) {
      mpEvent = item.value;
    }
    const tooltipData: TooltipData = mpEvent
      ? getTooltipDataForEvent(mpEvent, eventDefinitions, permissions, !this.attr(`hide-edit-controls`))
      : null;

    this.update({tooltipData});
  }

  getPersistenceKey() {
    return `events-menu`;
  }

  getEventTagsSection(): Section[] {
    const tagCounts = this.state.eventDefinitions.reduce((counts, def) => {
      if (!def.hidden) {
        def.tags.forEach((tag) => (counts[tag] = counts[tag] ? counts[tag] + 1 : 1));
      }

      return counts;
    }, {});

    const eventTagItems = Object.entries(tagCounts).map(([tag, count]) => ({
      label: `${tag} (${count})`,
      icon: `tag`,
      hasCaret: true,
      value: {
        type: `tag`,
        count,
        name: tag,
      },
    }));

    return [
      {
        label: `Events tagged as`,
        items: eventTagItems.sort(orderByLabel),
        showLimit: 7,
      },
    ];
  }

  getSections() {
    // TODO: Zihe remove this check, a quick and temp workaround for signal to display context panel
    if (this.attr(`sections`).length > 0) {
      return this.attr(`sections`);
    }
    const {isMenuLoading, selected, keyedEventData} = this.state;
    const selectedValue = selected ? selected.value : null;
    const eventData = this.state.eventData.map((ev) => ({...ev, isSelected: selectedValue === ev.value}));

    const ADDITIONAL_ITEMS_ATTR_MAPPING: {[p: string]: SectionItem[]} = {
      'show-any-event': [
        {
          ...ANY_EVENT,
          isSelected: !selectedValue,
        },
      ],
      'show-top-events': [
        {
          label: renameEvent(SPECIAL_EVENTS.TOP_EVENTS),
          value: SPECIAL_EVENTS.TOP_EVENTS,
          icon: `star-top-events`,
        },
      ],
      'show-all-events': [
        {
          label: renameEvent(SPECIAL_EVENTS.ALL_EVENTS),
          value: SPECIAL_EVENTS.ALL_EVENTS,
          icon: `star-top-events`,
        },
      ],
    };

    // Only allow show-session-event if not B2B
    if (!this.attr(`data-group-id`)) {
      ADDITIONAL_ITEMS_ATTR_MAPPING[`show-session-event`] = [SESSION_START_EVENT, SESSION_END_EVENT];
    }

    // picks additional items from attributes
    const additionalItems = Object.entries(ADDITIONAL_ITEMS_ATTR_MAPPING).reduce((items, [key, values]) => {
      if (this.attr(key)) {
        values = values.map((value) => ({
          ...value,
          isSelected: value.hasOwnProperty(`isSelected`) ? value.isSelected : value.value === selectedValue,
        }));

        return [...items, ...values];
      }

      return items;
    }, []);

    const section = {
      label: `Events`,
      isLoading: isMenuLoading,
      items: [...additionalItems, ...eventData],
    };

    const recentlyViewedSection = {
      label: `Recently viewed`,
      items: this.getRecentItems(ResourceType.Event).map((event) => ({
        ...event,
        ...(event.value ? keyedEventData[event.value] : {}),
      })),
    };

    return this.state.stagedTag
      ? [
          {
            ...section,
            label: `Events tagged as ${this.state.stagedTag.name}`,
            items: this.state.eventDefinitions
              .filter((def) => def.tags && def.tags.includes(this.state.stagedTag.name))
              .map((def) => section.items.find(({name}) => name === def.name))
              .filter(Boolean),
          },
        ]
      : [recentlyViewedSection, ...this.getEventTagsSection(), section];
  }

  selectedLabel() {
    const {selected} = this.state;

    if (selected) {
      const {renamed, label} = selected;

      // handle custom event loading
      if (selected.id && !selected.label) {
        const customEvent = this.state.eventData.find((event) => event.custom && event.id === selected.id);
        if (!customEvent) {
          return `Loading...`;
        } else {
          return customEvent.label;
        }
      }

      return renamed ? `${renamed} (${label})` : label;
    }

    return ``;
  }

  fetchEventDefinitions() {
    fetchEventDefinitions(this.sourceAttrs).then((eventDefinitions) => {
      this.update({eventDefinitions});
    });
  }

  // fetches top events and custom events simultaneously and appends
  // data as it comes, it is prefered to show something as soon as
  // it is available
  fetchEventData() {
    const {isMenuLoading} = this.state;
    const excludeCustomEvents = this.attr(`exclude-custom-events`);
    const excludeAutotrackEvents = this.attr(`exclude-autotrack-events`);
    const dataGroupId = this.attr(`data-group-id`);

    if (!this.projectID || isMenuLoading) {
      return;
    }

    try {
      this.setEventData([]);
      this.update({isMenuLoading: true});

      topEventsClient.query(this.sourceAttrs).then((events) => {
        events = events.map((ev) => ({
          ...ev,
          icon: getIconForEvent(ev),
          value: ev.name,
        }));
        this.updateEventItems(events);
      });

      if (!excludeCustomEvents) {
        customEventsClient.query(this.sourceAttrs).then((events) => {
          events = events.map(getCustomEventMenuItem);

          if (dataGroupId) {
            events = events.filter((ev) => !hasUserFilters(ev, events));
          }

          if (excludeAutotrackEvents) {
            events = getNonAutotrackCustomEvents(events, this.state.eventData);
          }

          this.updateEventItems(events);
        });
      }
    } catch (err) {
      console.error(`Error loading events items`, err);
    }
  }

  setEventData(events) {
    const eventData = sortBy(events, `label`);

    this.update({
      eventData,
      keyedEventData: keyBy(eventData, `value`),
    });
  }

  updateEventItems(items) {
    this.setEventData(this.state.eventData.concat(items));
    this.update({isMenuLoading: false});
  }

  trackingProps(selected) {
    return {
      eventName: `Select Event`,
      props: {
        'Event name': selected.label,
        'is custom event': selected.custom,
      },
    };
  }
}

export default registerMPElement(`mp-events-menu`, EventsMenu);
